'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "className"],
  _excluded2 = ["placement"],
  _excluded3 = ["placement"],
  _excluded4 = ["className"],
  _excluded5 = ["className"],
  _excluded6 = ["htmlSize"];
import { createContext } from "../react-context";
import { getValidChildren } from "../react-children-utils";
import { forwardRef, useMultiStyleConfig, omitThemingProps, chakra } from "../system";
import { cx } from "../shared-utils";
import { compact } from "../object-utils";
import { cloneElement } from 'react';
import { jsx } from 'react/jsx-runtime';
import { useFormControl } from "../form-control";

// src/input-group.tsx
var [InputGroupStylesProvider, useInputGroupStyles] = createContext({
  name: `InputGroupStylesContext`,
  errorMessage: `useInputGroupStyles returned is 'undefined'. Seems you forgot to wrap the components in "<InputGroup />" `
});
var InputGroup = forwardRef(function InputGroup2(props, ref) {
  const styles = useMultiStyleConfig("Input", props);
  const _omitThemingProps = omitThemingProps(props),
    {
      children,
      className
    } = _omitThemingProps,
    rest = _objectWithoutPropertiesLoose(_omitThemingProps, _excluded);
  const _className = cx("chakra-input__group", className);
  const groupStyles = {};
  const validChildren = getValidChildren(children);
  const input = styles.field;
  validChildren.forEach(child => {
    var _a, _b;
    if (!styles) return;
    if (input && child.type.id === "InputLeftElement") {
      groupStyles.paddingStart = (_a = input.height) != null ? _a : input.h;
    }
    if (input && child.type.id === "InputRightElement") {
      groupStyles.paddingEnd = (_b = input.height) != null ? _b : input.h;
    }
    if (child.type.id === "InputRightAddon") {
      groupStyles.borderEndRadius = 0;
    }
    if (child.type.id === "InputLeftAddon") {
      groupStyles.borderStartRadius = 0;
    }
  });
  const clones = validChildren.map(child => {
    var _a, _b;
    const theming = compact({
      size: ((_a = child.props) == null ? void 0 : _a.size) || props.size,
      variant: ((_b = child.props) == null ? void 0 : _b.variant) || props.variant
    });
    return child.type.id !== "Input" ? /*#__PURE__*/cloneElement(child, theming) : /*#__PURE__*/cloneElement(child, Object.assign(theming, groupStyles, child.props));
  });
  return /* @__PURE__ */jsx(chakra.div, Object.assign({
    className: _className,
    ref,
    __css: {
      width: "100%",
      display: "flex",
      position: "relative",
      isolation: "isolate"
    },
    "data-group": true
  }, rest, {
    children: /* @__PURE__ */jsx(InputGroupStylesProvider, {
      value: styles,
      children: clones
    })
  }));
});
InputGroup.displayName = "InputGroup";
var placements = {
  left: {
    marginEnd: "-1px",
    borderEndRadius: 0,
    borderEndColor: "transparent"
  },
  right: {
    marginStart: "-1px",
    borderStartRadius: 0,
    borderStartColor: "transparent"
  }
};
var StyledAddon = chakra("div", {
  baseStyle: {
    flex: "0 0 auto",
    width: "auto",
    display: "flex",
    alignItems: "center",
    whiteSpace: "nowrap"
  }
});
var InputAddon = forwardRef(function InputAddon2(props, ref) {
  var _a;
  const {
      placement = "left"
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded2);
  const placementStyles = (_a = placements[placement]) != null ? _a : {};
  const styles = useInputGroupStyles();
  return /* @__PURE__ */jsx(StyledAddon, Object.assign({
    ref
  }, rest, {
    __css: Object.assign({}, styles.addon, placementStyles)
  }));
});
InputAddon.displayName = "InputAddon";
var InputLeftAddon = forwardRef(function InputLeftAddon2(props, ref) {
  return /* @__PURE__ */jsx(InputAddon, Object.assign({
    ref,
    placement: "left"
  }, props, {
    className: cx("chakra-input__left-addon", props.className)
  }));
});
InputLeftAddon.displayName = "InputLeftAddon";
InputLeftAddon.id = "InputLeftAddon";
var InputRightAddon = forwardRef(function InputRightAddon2(props, ref) {
  return /* @__PURE__ */jsx(InputAddon, Object.assign({
    ref,
    placement: "right"
  }, props, {
    className: cx("chakra-input__right-addon", props.className)
  }));
});
InputRightAddon.displayName = "InputRightAddon";
InputRightAddon.id = "InputRightAddon";
var StyledInputElement = chakra("div", {
  baseStyle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "absolute",
    top: "0",
    zIndex: 2
  }
});
var InputElement = forwardRef(function InputElement2(props, ref) {
  var _a, _b;
  const {
      placement = "left"
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded3);
  const styles = useInputGroupStyles();
  const input = styles.field;
  const attr = placement === "left" ? "insetStart" : "insetEnd";
  const elementStyles = Object.assign({
    [attr]: "0",
    width: (_a = input == null ? void 0 : input.height) != null ? _a : input == null ? void 0 : input.h,
    height: (_b = input == null ? void 0 : input.height) != null ? _b : input == null ? void 0 : input.h,
    fontSize: input == null ? void 0 : input.fontSize
  }, styles.element);
  return /* @__PURE__ */jsx(StyledInputElement, Object.assign({
    ref,
    __css: elementStyles
  }, rest));
});
InputElement.id = "InputElement";
InputElement.displayName = "InputElement";
var InputLeftElement = forwardRef(function InputLeftElement2(props, ref) {
  const {
      className
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded4);
  const _className = cx("chakra-input__left-element", className);
  return /* @__PURE__ */jsx(InputElement, Object.assign({
    ref,
    placement: "left",
    className: _className
  }, rest));
});
InputLeftElement.id = "InputLeftElement";
InputLeftElement.displayName = "InputLeftElement";
var InputRightElement = forwardRef(function InputRightElement2(props, ref) {
  const {
      className
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded5);
  const _className = cx("chakra-input__right-element", className);
  return /* @__PURE__ */jsx(InputElement, Object.assign({
    ref,
    placement: "right",
    className: _className
  }, rest));
});
InputRightElement.id = "InputRightElement";
InputRightElement.displayName = "InputRightElement";

// src/input.tsx
var Input = forwardRef(function Input2(props, ref) {
  const {
      htmlSize
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded6);
  const styles = useMultiStyleConfig("Input", rest);
  const ownProps = omitThemingProps(rest);
  const input = useFormControl(ownProps);
  const _className = cx("chakra-input", props.className);
  return /* @__PURE__ */jsx(chakra.input, Object.assign({
    size: htmlSize
  }, input, {
    __css: styles.field,
    ref,
    className: _className
  }));
});
Input.displayName = "Input";
Input.id = "Input";
export { Input, InputAddon, InputGroup, InputLeftAddon, InputLeftElement, InputRightAddon, InputRightElement, useInputGroupStyles };